import { useState } from "react";
import Image from "next/image";

import {
  Box,
  Center,
  Divider,
  Flex,
  Input,
  Text,
  useColorMode,
} from "@chakra-ui/react";

import { useAPY } from "dapp-sdk";

import Link from "@components/Link";

// Utils
import { trackEvent } from "utils/firebase";
import { StrapiSectionDetail } from "@customtypes/global";

import { defiCurrencyIconMapping } from "@constants/dappWalletMapping";
import { STAKE_REWARD_CALCULATOR } from "@constants/analytics";

import Section from "../Section";

interface CalculatorProps {
  sectionDetails: StrapiSectionDetail;
}

const RewardCalculator = ({ sectionDetails }: CalculatorProps) => {
  const { colorMode } = useColorMode();
  const [amount, setAmount] = useState<undefined | number>(100);
  const apyData = useAPY({
    enabled: true,
  });
  const apy = apyData?.apy ? Number(apyData.apy.toFixed(4)) : 0;

  const earning = amount && apy ? +((amount * apy) / 100).toFixed(4) : 0;

  const monthlyEarning = amount && earning ? +(earning / 12).toFixed(4) : 0;

  const yearlyEarning = amount && earning ? earning : 0;

  const token = process.env.NEXT_PUBLIC_TOKEN;

  return (
    <Section
      title={sectionDetails.title}
      subTitle={sectionDetails.sub_title}
      id={sectionDetails.section_id}
    >
      <Center>
        <Box
          mt={{ base: "40px", lg: "64px" }}
          p="2rem"
          fontSize={14}
          bg="#E6F8EF"
          borderRadius={24}
          w="100%"
          color="textSecondary"
          textAlign="left"
          minW={{ base: "inherit", lg: "536px" }}
        >
          <Text>{`Enter ${token} amount`}</Text>
          <Input
            bg="white"
            h="96px"
            p="1.5rem"
            borderColor="primary"
            borderRadius={16}
            mt="1rem"
            color="textPrimary"
            type="number"
            min={0}
            value={amount}
            fontSize={32}
            onChange={(e) =>
              setAmount(e.target.value ? +e.target.value : undefined)
            }
            _focus={{
              boxShadow: "none",
              outline: "none",
              borderColor: "primary",
              borderWidth: "1px",
            }}
            _hover={{
              borderColor: "primary",
              borderWidth: "1px",
            }}
            fontWeight="600"
          />
          <Flex mt="1rem" align="center" justify="space-between">
            <Text>APY</Text>
            <Text fontWeight="600">{apy} %</Text>
          </Flex>
          <Divider my="1rem" borderColor="textSecondary" />
          <Flex align="center" justify="space-between" fontWeight="600">
            <Text>Monthly earning</Text>
            <Flex align="center" gap="0.5rem">
              <Image
                src={
                  defiCurrencyIconMapping[
                    process.env.NEXT_PUBLIC_TOKEN?.toLowerCase() || ""
                  ][colorMode].src
                }
                height="24px"
                width="24px"
                alt="blockchain-image"
              />
              <Text color="textPrimary">
                {monthlyEarning} {token}
              </Text>
            </Flex>
          </Flex>
          <Flex
            mt="20px"
            align="center"
            justify="space-between"
            fontWeight="600"
          >
            <Text>Yearly earning</Text>
            <Flex align="center" gap="0.5rem">
              <Image
                src={
                  defiCurrencyIconMapping[
                    process.env.NEXT_PUBLIC_TOKEN?.toLowerCase() || ""
                  ][colorMode].src
                }
                height="24px"
                width="24px"
                alt="blockchain-image"
              />
              <Text color="textPrimary">
                {yearlyEarning} {token}
              </Text>
            </Flex>
          </Flex>
          {token !== "ETH" && (
            <Link link="/stake">
              <Box
                mt="36px"
                w="100%"
                bg="#07C166"
                borderRadius={8}
                p="1rem 45px"
                cursor="pointer"
                onClick={() => trackEvent(STAKE_REWARD_CALCULATOR)}
              >
                <Text
                  textAlign="center"
                  color="white"
                  fontWeight="600"
                  fontSize={16}
                >
                  {`Stake ${token}`}
                </Text>
              </Box>
            </Link>
          )}
        </Box>
      </Center>
    </Section>
  );
};

export default RewardCalculator;
